/**
 * Court Intelligence API client for Phase 3E.
 * Provides methods for court intelligence preview, matter intelligence,
 * and form fill operations.
 *
 * Includes transform functions that normalize backend response shapes
 * into the frontend component contracts defined in courtIntelligence.types.ts.
 */
import { apiClient } from '@/config/api';
import type {
  CourtIntelligencePreview,
  CourtLifecycleStatus,
  MatterCourtIntelligence,
  MatterCourtProvenance,
  MatterCourtForm,
  MatterCourtJudge,
  MatterCourtFee,
  MatterCourtDeadline,
  BatchFillRequest,
  BatchFillResult,
  DeadlineCalculationTrace,
  CatalogCourtForm,
} from '../types/courtIntelligence.types';

const COURT_INTEL_BASE = '/api/v1/court-intelligence';
const MATTERS_BASE = '/api/v1/matters';
const COURT_FORMS_BASE = '/api/v1/court-forms';

export interface FillToDmsRequest {
  context_type: 'matter' | 'client';
  matter_id?: string;
  client_id?: string;
  task_id?: string;
  jurisdiction_code?: string;
  version_description?: string;
}

export interface MaterializeToDmsRequest {
  context_type: 'matter' | 'client';
  matter_id?: string;
  client_id?: string;
  task_id?: string;
  title?: string;
  description?: string;
}

export interface MaterializeToDmsResult {
  created_new: boolean;
  binding: {
    binding_id: string;
    court_form_id: string;
    document_id: string;
    context_type: 'matter' | 'client';
    client_id: string | null;
    matter_id: string | null;
    task_id: string | null;
    source_form_number: string;
    source_jurisdiction_code: string | null;
    created_at: string;
    updated_at: string;
  };
  document: {
    document_id: string;
    latest_version_id: string | null;
    title: string;
    client_id: string | null;
    matter_id: string | null;
  };
}

export interface CourtFormContextBinding {
  binding_id: string;
  court_form_id: string;
  document_id: string;
  context_type: 'matter' | 'client';
  client_id: string | null;
  matter_id: string | null;
  task_id: string | null;
  source_form_number: string;
  source_jurisdiction_code: string | null;
  created_at: string;
  updated_at: string;
}

export interface FillToDmsResult {
  created_new_document: boolean;
  binding: {
    binding_id: string;
    court_form_id: string;
    document_id: string;
    context_type: 'matter' | 'client';
    client_id: string | null;
    matter_id: string | null;
    task_id: string | null;
    source_form_number: string;
    source_jurisdiction_code: string | null;
    created_at: string;
    updated_at: string;
  };
  document: {
    document_id: string;
    latest_version_id: string | null;
    title: string;
    client_id: string | null;
    matter_id: string | null;
  };
  version: {
    version_id: string;
    version_number: string;
    edit_status: string;
    uploaded_at: string;
  };
  fill_summary: {
    form_number: string;
    filled_fields_count: number;
    skipped_mappings: number;
    missing_pdf_fields: number;
    xfa_unsupported: boolean;
  };
}

// ---------------------------------------------------------------------------
// Transform: Preview API response → CourtIntelligencePreview
// ---------------------------------------------------------------------------
function transformPreviewResponse(raw: any): CourtIntelligencePreview {
  // Derive overall lifecycle status from judges/fees sections
  const judgesStatus: CourtLifecycleStatus = raw.judges?.lifecycle_status || 'draft';
  const feesStatus: CourtLifecycleStatus = raw.fees?.lifecycle_status || 'draft';
  const overallStatus: CourtLifecycleStatus =
    judgesStatus === 'stale' || feesStatus === 'stale'
      ? 'stale'
      : judgesStatus === 'validated' && feesStatus === 'validated'
        ? 'validated'
        : 'draft';

  // Transform form samples — backend has different fields than frontend expects
  const formSamples = (raw.forms?.sample || []).map((f: any) => ({
    form_number: f.form_number || '',
    form_name: f.form_name || '',
    is_mandatory: f.is_mandatory || false,
    field_count: f.field_count || 0,
    lifecycle_status: f.lifecycle_status || 'draft',
  }));

  // Extract judge names from raw extracted data objects
  const judgeSamples = (raw.judges?.sample || []).map((j: any) => ({
    name: j.name || j.judge_name || 'Unknown',
    department: j.department || j.dept || undefined,
  }));

  // Extract fee data from raw extracted data objects
  const feeSamples = (raw.fees?.sample || []).map((f: any) => ({
    description: f.description || f.fee_description || f.name || 'Court fee',
    amount: typeof f.amount === 'number' ? f.amount : parseFloat(f.amount) || 0,
    currency: f.currency || 'USD',
  }));

  // Transform template match → template summary
  const templateMatch = raw.template_match || {};
  const templateSummary: CourtIntelligencePreview['template_summary'] = {
    available_templates: templateMatch.matched ? 1 : 0,
    ...(templateMatch.matched
      ? {
          best_match: {
            id: templateMatch.id || '',
            name: templateMatch.name || '',
            task_count: templateMatch.task_count || 0,
            phase_count: templateMatch.phase_count || 0,
            estimated_duration_days: templateMatch.estimated_duration_days ?? null,
            is_firm_variant: templateMatch.is_firm_variant || false,
            parent_template_id: templateMatch.parent_template_id || null,
          },
        }
      : {}),
  };

  // Transform subtype data if present
  const subtypeData = raw.subtype ? {
    key: raw.subtype.key || '',
    label: raw.subtype.label || null,
    forms_total: raw.subtype.forms_total || 0,
    forms_fillable: raw.subtype.forms_fillable || 0,
    forms_sample: (raw.subtype.forms_sample || []).map((f: any) => ({
      form_number: f.form_number || '',
      form_name: f.form_name || '',
      field_count: f.field_count || 0,
      lifecycle_status: f.lifecycle_status || 'draft',
    })),
    phases: (raw.subtype.phases || []).map((p: any) => ({
      name: p.name || '',
      order: p.order || 0,
      tasks: (p.tasks || []).map((t: any) => ({
        title: t.title || '',
        task_type: t.task_type,
        is_court_mandated: t.is_court_mandated,
        due_date_offset_days: t.due_date_offset_days,
        priority: t.priority,
        forms: t.forms,
      })),
    })),
  } : null;

  return {
    jurisdiction_code: raw.jurisdiction_code || '',
    practice_area: raw.practice_area || '',
    court_name: raw.court_name || null,
    lifecycle_status: overallStatus,
    forms: {
      total: raw.forms?.total || 0,
      mandatory_count: raw.forms?.mandatory_count || 0,
      fillable_count: raw.forms?.fillable_count || 0,
      sample: formSamples,
    },
    judges: {
      total: raw.judges?.total || 0,
      sample: judgeSamples,
    },
    fees: {
      total: raw.fees?.total || 0,
      sample: feeSamples,
    },
    template_summary: templateSummary,
    subtype: subtypeData,
  };
}

// ---------------------------------------------------------------------------
// Transform: Matter Intelligence API response → MatterCourtIntelligence
// ---------------------------------------------------------------------------
function transformMatterIntelligenceResponse(raw: any): MatterCourtIntelligence {
  const matter = raw.matter || {};
  const lifecycleSummary = raw.lifecycle_summary || {};

  // Derive overall data quality from lifecycle summary
  const statuses: CourtLifecycleStatus[] = [
    lifecycleSummary.forms || 'draft',
    lifecycleSummary.judges || 'draft',
    lifecycleSummary.fees || 'draft',
  ];
  const dataQuality: CourtLifecycleStatus | 'unavailable' =
    statuses.includes('stale')
      ? 'stale'
      : statuses.every((s) => s === 'validated')
        ? 'validated'
        : 'draft';

  // Build task→form links lookup for linked_task_ids
  const taskFormLinks: Array<{ task_id: string; form_number: string; is_mandatory: boolean }> =
    raw.task_form_links || [];
  const formToTaskIds = new Map<string, string[]>();
  const formMandatoryMap = new Map<string, boolean>();
  for (const link of taskFormLinks) {
    const fnum = (link.form_number || '').toUpperCase();
    if (!formToTaskIds.has(fnum)) formToTaskIds.set(fnum, []);
    formToTaskIds.get(fnum)!.push(link.task_id);
    if (link.is_mandatory) formMandatoryMap.set(fnum, true);
  }

  // Transform forms
  const forms: MatterCourtForm[] = (raw.forms || []).map((f: any) => {
    const fnum = (f.form_number || '').toUpperCase();
    return {
      form_number: f.form_number || '',
      form_name: f.form_name || '',
      category: f.category || null,
      is_mandatory: formMandatoryMap.get(fnum) || false,
      field_count: f.field_count || 0,
      lifecycle_status: f.lifecycle_status || 'draft',
      is_fillable: f.is_fillable ?? (f.pdf_url ? true : false),
      fill_coverage: f.fill_coverage ?? null,
      linked_task_ids: formToTaskIds.get(fnum) || [],
    };
  });

  // Transform judges from raw extracted data
  const judges: MatterCourtJudge[] = (raw.judges || []).map((j: any) => ({
    name: j.name || j.judge_name || 'Unknown',
    department: j.department || j.dept || null,
    lifecycle_status: j.lifecycle_status || 'draft',
  }));

  // Transform fees from raw extracted data
  const fees: MatterCourtFee[] = (raw.fees || []).map((f: any) => ({
    description: f.description || f.fee_description || f.name || 'Court fee',
    amount: typeof f.amount === 'number' ? f.amount : parseFloat(f.amount) || 0,
    currency: f.currency || 'USD',
    fee_type: f.fee_type || f.type || null,
    lifecycle_status: f.lifecycle_status || 'draft',
  }));

  // Transform deadline traces → MatterCourtDeadline[]
  const deadlines: MatterCourtDeadline[] = (raw.deadline_traces || []).map((dt: any) => {
    const calcTrace: DeadlineCalculationTrace | null = dt.calculation_trace
      ? {
          trigger_event: dt.trigger_event,
          trigger_date: dt.trigger_date,
          adjustments: (dt.calculation_trace || []).map((step: any) => ({
            reason: step.description || step.step || '',
            days_added: step.days_adjusted || 0,
            resulting_date: step.date_after,
            adjusted_date: step.date_after,
            original_date: step.date_before,
          })),
          final_date: dt.calculated_due_date || dt.final_date || '',
          statute_citation: dt.statute_citation || '',
          warnings: dt.warnings || [],
          calculation_trace: dt.calculation_trace,
        }
      : null;

    return {
      task_id: dt.task_id || '',
      task_title: dt.task_title || '',
      due_date: dt.due_date || dt.calculated_due_date || null,
      is_statutory: true,
      statute_reference: dt.statute_citation || null,
      calculation_trace: calcTrace,
    };
  });

  const extractProvenance = (src: any): MatterCourtProvenance | null => {
    if (!src) return null;
    return {
      extracted_at: src.extracted_at || null,
      validated_at: src.validated_at || null,
      stale_detected_at: src.stale_detected_at || null,
      stale_reason: src.stale_reason || null,
      last_crawled_at: src.last_crawled_at || null,
    };
  };

  const rawProvenance = raw.provenance || {};

  // Transform catalog forms
  const catalogForms: CatalogCourtForm[] = (raw.catalog_forms || []).map((cf: any) => ({
    form_number: cf.form_number || '',
    form_name: cf.form_name || '',
    category: cf.category || null,
    lifecycle_status: cf.lifecycle_status || 'draft',
    field_count: cf.field_count || 0,
    is_in_subtype: cf.is_in_subtype ?? false,
    is_in_matter: cf.is_in_matter ?? false,
    subtypes: Array.isArray(cf.subtypes) ? cf.subtypes : [],
  }));

  return {
    matter_id: matter.matter_id || '',
    jurisdiction_code: matter.jurisdiction_code || null,
    practice_area: matter.practice_area || null,
    court_name: null,
    case_subtype: raw.case_subtype || null,
    data_quality: dataQuality,
    forms,
    catalog_forms: catalogForms,
    judges,
    judges_unfiltered: raw.judges_unfiltered ?? false,
    fees,
    deadlines,
    provenance: {
      registry: extractProvenance(rawProvenance.registry),
      judges_dataset: extractProvenance(rawProvenance.judges_dataset),
      fees_dataset: extractProvenance(rawProvenance.fees_dataset),
    },
    generated_at: raw.generated_at || null,
  };
}

// ---------------------------------------------------------------------------
// Transform: Batch Fill API response → BatchFillResult
// ---------------------------------------------------------------------------
function transformBatchFillResponse(raw: any): BatchFillResult {
  // Backend returns { generated: [...], failed: [...] }
  // Frontend expects { results: [...], download_all_token }
  const generated = (raw.generated || []).map((g: any) => ({
    form_number: g.form_number || '',
    success: true,
    filled_fields_count: g.filled_fields_count || 0,
    skipped_fields_count: g.skipped_mappings || 0,
    download_token: g.token || '',
  }));

  const failed = (raw.failed || []).map((f: any) => ({
    form_number: f.form_number || '',
    success: false,
    error: f.error || 'Unknown error',
    filled_fields_count: 0,
    skipped_fields_count: 0,
    download_token: '',
  }));

  return {
    results: [...generated, ...failed],
    download_all_token: generated.length > 0 ? generated[0].download_token : '',
    expires_in_seconds: raw.expires_in_seconds || 900,
  };
}

// ---------------------------------------------------------------------------
// API Client
// ---------------------------------------------------------------------------
export const courtIntelligenceApi = {
  /**
   * Get court intelligence preview for a jurisdiction + practice area.
   * Used in MatterCreateForm to show what data is available before creating a matter.
   */
  getPreview: async (params: {
    jurisdiction_code: string;
    practice_area: string;
    state_code?: string;
    case_subtype?: string;
  }): Promise<CourtIntelligencePreview> => {
    const raw = await apiClient.get(
      `${COURT_INTEL_BASE}/preview`,
      params as Record<string, unknown>
    );
    return transformPreviewResponse(raw);
  },

  /**
   * Get court intelligence for an existing matter.
   * Returns forms, judges, fees, and deadline traces for the matter's jurisdiction.
   */
  getMatterIntelligence: async (matterId: string): Promise<MatterCourtIntelligence> => {
    const raw = await apiClient.get(
      `${MATTERS_BASE}/${matterId}/court-intelligence`
    );
    return transformMatterIntelligenceResponse(raw);
  },

  /**
   * Fill a single court form for a matter.
   * Returns the filled PDF as a Blob.
   */
  fillForm: async (
    matterId: string,
    formNumber: string,
    jurisdictionCode?: string
  ): Promise<Blob> => {
    const keycloak = (window as any).keycloak;
    const token = keycloak?.token;

    const response = await fetch(
      `/api/v1/matters/${matterId}/forms/${encodeURIComponent(formNumber)}/fill`,
      {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...(jurisdictionCode ? { jurisdiction_code: jurisdictionCode } : {}),
        }),
      }
    );

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');
      throw new Error(`Form fill failed: ${response.status} - ${errorText}`);
    }

    return response.blob();
  },

  /**
   * Fill multiple court forms for a matter in a single request.
   * Returns normalized results with download tokens.
   */
  fillBatch: async (matterId: string, request: BatchFillRequest): Promise<BatchFillResult> => {
    const raw = await apiClient.post(
      `${MATTERS_BASE}/${matterId}/forms/fill-batch`,
      request
    );
    return transformBatchFillResponse(raw);
  },

  /**
   * Download a previously filled PDF using a temporary download token.
   * Returns the PDF as a Blob.
   */
  downloadFilled: async (token: string): Promise<Blob> => {
    const keycloak = (window as any).keycloak;
    const authToken = keycloak?.token;

    const response = await fetch(
      `${COURT_FORMS_BASE}/download/${token}`,
      {
        headers: {
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Download failed: ${response.status}`);
    }

    return response.blob();
  },

  /**
   * Batch lookup: resolve form_numbers to their PDF URLs from the court_forms catalog.
   * Returns a map of FORM_NUMBER → { id, pdf_url, detail_url, form_name, lifecycle_status }.
   */
  lookupFormUrls: async (
    formNumbers: string[],
  ): Promise<Record<string, { id: string; pdf_url: string | null; detail_url: string | null; form_name: string; lifecycle_status: string }>> => {
    if (!formNumbers || formNumbers.length === 0) return {};
    const raw = await apiClient.post(
      `${COURT_FORMS_BASE}/lookup`,
      { form_numbers: formNumbers },
    );
    return (raw as unknown as Record<string, { id: string; pdf_url: string | null; detail_url: string | null; form_name: string; lifecycle_status: string }>) || {};
  },

  /**
   * Fill a canonical court form and persist the output as a new DMS version
   * within the selected context document (materializes first if needed).
   */
  fillToDms: async (
    courtFormId: string,
    request: FillToDmsRequest,
  ): Promise<FillToDmsResult> => {
    const raw = await apiClient.post(
      `${COURT_FORMS_BASE}/${encodeURIComponent(courtFormId)}/fill-to-dms`,
      request as unknown as Record<string, unknown>,
    );
    return raw as unknown as FillToDmsResult;
  },

  /**
   * Materialize a canonical court form into a DMS document for the selected context.
   */
  materializeToDms: async (
    courtFormId: string,
    request: MaterializeToDmsRequest,
  ): Promise<MaterializeToDmsResult> => {
    const raw = await apiClient.post(
      `${COURT_FORMS_BASE}/${encodeURIComponent(courtFormId)}/materialize`,
      request as unknown as Record<string, unknown>,
    );
    return raw as unknown as MaterializeToDmsResult;
  },

  /**
   * List all court-form DMS bindings for a context.
   */
  listContextBindings: async (params: {
    context_type: 'matter' | 'client';
    matter_id?: string;
    client_id?: string;
    task_id?: string;
  }): Promise<CourtFormContextBinding[]> => {
    const raw = await apiClient.get(
      `${COURT_FORMS_BASE}/bindings`,
      params as Record<string, unknown>,
    );
    const payload = raw as { bindings?: CourtFormContextBinding[] } | undefined;
    return Array.isArray(payload?.bindings) ? payload!.bindings : [];
  },
};

/**
 * Utility: Open a filled PDF in a new browser tab.
 * Creates a temporary Blob URL and opens it.
 */
export function openPdfInNewTab(blob: Blob, filename?: string): void {
  const url = URL.createObjectURL(blob);
  const win = window.open(url, '_blank');
  // Clean up the blob URL after the window loads (or after a timeout if blocked)
  if (win) {
    win.addEventListener('load', () => {
      // Keep URL alive while window is open — revoke on close
      win.addEventListener('beforeunload', () => URL.revokeObjectURL(url));
    });
  } else {
    // Popup blocked — fallback to download
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'filled-form.pdf';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  }
}
